// ProductDetailsPage.js
import React, { useState, useEffect } from 'react';
import apiClient from '../../networking/axiosconfig';
import { useSelector } from 'react-redux';
import './ProductDetailsPage.css';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Button } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';

const SalesDetailsPage = () => {
    const { id } = useParams();
    const { data: config } = useSelector((state) => state.config);
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchProductDetails = async () => {
            try {
                const response = await apiClient.get(`${config.API_BASE_URL}/api/products/${id}`);
                setProduct(response.data);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching product details:', error);
                setError(error.message || 'Failed to fetch product details');
                setLoading(false);
            }
        };

        if (config && config.API_BASE_URL) { // Ensure config is loaded before fetching
            fetchProductDetails();
        }
    }, [id, config]); // Removed config.API_BASE_URL from dependencies, config object itself is enough

    if (loading) {
        return <div>Loading product details...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    if (!product) {
        return <div>Product not found</div>;
    }

    const nextImage = () => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % product.images.length);
    };

    const prevImage = () => {
        setCurrentImageIndex((prevIndex) => (prevIndex - 1 + product.images.length) % product.images.length);
    };

    const handleGoBack = () => {
        navigate(-1); // Go back to the previous page in history
    };

    return (
        <div>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleGoBack}
                style={{ marginBottom: '0px' ,marginLeft:'16px'}}
            >
                Back
            </Button>
            <div className="product-details-container">
                <div className="column">
                    {/* Combined Product Details and Specifications Card */}
                    <div className="card product-info-card">
                        <div className="tables-container"> {/* New container for the tables */}
                            <table className="details-table">
                                <thead>
                                    <tr>
                                        <th colSpan="2">Details</th> {/* Add a header for the specifications table */}
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <th>Name</th>
                                        <td>{product.name}</td>
                                    </tr>
                                    <tr>
                                        <th>Brand</th>
                                        <td>{product.brand}</td>
                                    </tr>
                                    <tr>
                                        <th>Price</th>
                                        <td>₹{product.price}</td>
                                    </tr>
                                    {product.discountPercentage > 0 && (
                                        <tr>
                                            <th>Discount</th>
                                            <td>{product.discountPercentage}%</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <th>Description</th>
                                        <td style={{ whiteSpace: 'pre-line' }}>{product.description}</td> {/* Prevent wrapping for better side-by-side view */}
                                    </tr>
                                    {product.highlights && (
                                        <tr>
                                            <th>Highlights</th>
                                            <td>{product.highlights}</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <th>Delivery Time</th>
                                        <td>{product.deliveryTime}</td>
                                    </tr>
                                    {product.isFeatured && (
                                        <tr>
                                            <th>Featured</th>
                                            <td>Yes</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <th>Stock</th>
                                        <td>{product.stock}</td>
                                    </tr>
                                    <tr>
                                        <th>Store Code</th>
                                        <td>{product.storeCode}</td>
                                    </tr>
                                    {/* Add other relevant details here in table rows */}
                                </tbody>
                            </table>

                            <table className="specifications-table">
                                <thead>
                                    <tr>
                                        <th colSpan="2">Specifications</th> {/* Add a header for the specifications table */}
                                    </tr>
                                </thead>
                                <tbody>
                                    {product.specifications && Object.entries(product.specifications).map(([key, value]) => (
                                        <tr key={key}>
                                            <th>{key}</th>
                                            <td>{value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Colors Card */}
                    <div className="card colors-card">
                        <h3>Available Colors</h3>
                        <ul className="colors-grid">
                            {product.colors && product.colors.map((color) => {
                                const isWhite = color.toLowerCase() === 'white';
                                const chipStyle = {
                                    backgroundColor: color,
                                    color: isWhite ? 'black' : 'white',
                                    border: isWhite ? '1px solid #ccc' : 'none',
                                };
                                return (
                                    <li key={color} className="color-chip" style={chipStyle}>
                                        {color}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>

                <div className="column">
                    {/* Images Card with Carousel */}
                    <div className="card images-card">
                        <h3>Product Images</h3>
                        {product.images && product.images.length > 0 ? (
                            <div className="image-carousel">
                                <button className="carousel-button prev" onClick={prevImage}>&lt;</button>
                                <img src={product.images[currentImageIndex]} alt={product.name} style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }} />
                                <button className="carousel-button next" onClick={nextImage}>&gt;</button>
                                <div className="carousel-dots">
                                    {product.images.map((_, index) => (
                                        <span
                                            key={index}
                                            className={`dot ${index === currentImageIndex ? 'active' : ''}`}
                                            onClick={() => setCurrentImageIndex(index)}
                                        ></span>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <p>No images available.</p>
                        )}
                    </div>

                    {/* Content Card */}
                    {product.content && (
                        <div className="card content-card">
                            <h3>Content</h3>
                            <div dangerouslySetInnerHTML={{ __html: product.content }} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SalesDetailsPage;